// AI-CONTEXT: See docs/features/participation-value-dashboard-card.md
import currentLadderImpact from './currentLadderImpact'
import defaultParticipationParameters from './defaultParticipationParameters'

/**
 * @typedef {object} CurrentValueSummary
 * @property {number} value - Bruto: som van het verschil beginsituatie → huidige situatie (per jaar)
 * @property {number} attributedValue - Toegerekend aan de interventie (MSI-rekenregels) — het getal van de kaart
 * @property {number} counted - Deelnemers die doorgerekend konden worden
 * @property {number} notAttributedCount - Deelnemers zonder looptijd (duur of uren onbekend)
 * @property {Object<string, number>} notAttributedReasons - Dezelfde telling per reden
 * @property {Array<{id: string, name: string, reason: string}>} notAttributed - Wie dat zijn
 * @property {Array<{id: string, name: string}>} withoutCurrentSituation - Wel een beginsituatie, geen hoofdactiviteit in de huidige situatie
 */

/**
 * De maatschappelijke waarde van de participatieladder, stand van nu, over
 * alle deelnemers. Alleen wie een ingevulde huidige situatie heeft telt mee —
 * een lege huidige situatie leest als "geen werk, geen uitkering" en zou een
 * fantoombesparing tonen.
 *
 * Sinds #343 (besluit Gijs 2026-11-04) is het getal van de kaart de waarde die
 * aan de interventie wordt toegerekend (impactclaim, looptijd, drop-off,
 * disconto). Wie geen looptijd heeft, telt niet mee en staat bij naam in de
 * samenvatting — nooit als 0 (besluit R2).
 *
 * Spiegel: functions/participationHelpers/sumCurrentSocietalValue.js (pariteitstest).
 *
 * @param {Array<{ID?: string, Name?: string, Participation?: {t0?: object, t1?: object}}>} personas
 * @param {object} [parameters] - participation model parameters
 * @param {Object<string, string>|null} [birthDates] - PersonaID → geboortedatum (birthDatesByPersona), voor de leeftijdsovergang van 2026.3
 * @param {{ Months?: *, HoursPerWeek?: * }|null} [orgDefault] - CompagnyMeta.DefaultInterventionDose
 * @returns {CurrentValueSummary}
 */
const sumCurrentSocietalValue = (personas, parameters = defaultParticipationParameters, birthDates = null, orgDefault = null) => {
    const summary = {
        value: 0, attributedValue: 0, counted: 0, notAttributedCount: 0, notAttributedReasons: {}, notAttributed: [], withoutCurrentSituation: [],
    }
    if (!Array.isArray(personas)) return summary

    personas.forEach((persona) => {
        const id = persona?.ID || ''
        const name = persona?.Name || 'Naamloos'
        const current = currentLadderImpact({ persona, parameters, birthDates, orgDefault })
        if (!current) {
            if (persona?.Participation?.t0?.mainActivity) summary.withoutCurrentSituation.push({ id, name })
            return
        }
        const { delta } = current.impact
        summary.value += Number.isFinite(delta.totalSocietalValue) ? delta.totalSocietalValue : 0
        if (delta.attributedSocietalValue === null) {
            const reason = current.dose.reason
            summary.notAttributedCount += 1
            summary.notAttributedReasons[reason] = (summary.notAttributedReasons[reason] || 0) + 1
            summary.notAttributed.push({ id, name, reason })
            return
        }
        summary.counted += 1
        summary.attributedValue += delta.attributedSocietalValue
    })
    return summary
}

export default sumCurrentSocietalValue
